import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { savePortfolioPromptToArtifact, type PromptGeneration } from "../../api/client";
import SimpleMarkdown from "../SimpleMarkdown";

interface Props {
  generation: PromptGeneration;
  onRegenerate: () => void;
  onRetry?: () => void;
}

export default function GeneratedPromptView({ generation, onRegenerate, onRetry }: Props) {
  const theme = useTheme();
  const { t } = useTranslation("portfolio-kit");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const saveMutation = useMutation({
    mutationFn: () => savePortfolioPromptToArtifact(generation.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolioArtifacts"] });
      queryClient.invalidateQueries({ queryKey: ["portfolioPromptGenerations"] });
    },
  });
  // Reflect a just-succeeded mutation immediately, before the refetched
  // generation prop flips artifact_id — closes a double-submit window.
  const isSaved = generation.artifact_id != null || saveMutation.isSuccess;
  const pkg = generation.generated_package;
  const report = generation.validation_report;
  const readiness = report?.readiness_score;
  // Band the advisory score for a quick at-a-glance signal.
  const readinessColor: "success" | "warning" | "default" =
    readiness == null ? "default" : readiness >= 85 ? "success" : readiness >= 70 ? "warning" : "default";

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  }

  function download(filename: string, content: string) {
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!pkg) {
    return (
      <Alert
        severity="error"
        action={
          <>
            {onRetry && (
              <Button color="inherit" size="small" onClick={onRetry}>
                {t("GeneratedPromptView.retryButton")}
              </Button>
            )}
            <Button color="inherit" size="small" onClick={onRegenerate}>
              {t("GeneratedPromptView.startOverButton")}
            </Button>
          </>
        }
      >
        {t("GeneratedPromptView.generationFailedBody")}
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      {/* 1. Validation warning */}
      {report && !report.passed && (
        <Alert
          severity="warning"
          action={
            <Button onClick={onRegenerate}>
              {t("GeneratedPromptView.regenerateButton")}
            </Button>
          }
        >
          {t("GeneratedPromptView.qualityChecksFailed", {
            checks: report.checks
              .filter((c) => !c.passed)
              .map((c) => c.name)
              .join(", "),
          })}
        </Alert>
      )}

      {/* 2. Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap" rowGap={1}>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="h5" fontWeight={500}>{t("GeneratedPromptView.headerTitle")}</Typography>
          {readiness != null && (
            <Tooltip title={t("GeneratedPromptView.readinessTooltip")}>
              <Chip
                label={t("GeneratedPromptView.readinessChip", { readiness })}
                size="small"
                color={readinessColor}
                variant={readinessColor === "default" ? "outlined" : "filled"}
              />
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          {isSaved ? (
            <>
              <Chip
                icon={<CheckCircleIcon />}
                label={t("GeneratedPromptView.savedChip")}
                size="small"
                color="success"
                variant="outlined"
              />
              <Button variant="outlined" onClick={() => navigate("/portfolio-kit")}>
                {t("GeneratedPromptView.viewInYourWorkButton")}
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              startIcon={<BookmarkAddIcon />}
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending
                ? t("GeneratedPromptView.savingButton")
                : t("GeneratedPromptView.saveToYourWorkButton")}
            </Button>
          )}
          <Button variant="outlined" onClick={onRegenerate}>{t("GeneratedPromptView.startOverButton")}</Button>
        </Stack>
      </Stack>

      {/* Save error — non-blocking, doesn't crash the view */}
      {saveMutation.isError && (
        <Alert severity="error">
          {t("GeneratedPromptView.saveErrorMessage")}
        </Alert>
      )}

      {/* 3. Primary action: copy all */}
      <Button
        variant="contained"
        startIcon={<ContentCopyIcon />}
        onClick={() => copy(pkg.blocks.map((b) => b.prompt).join("\n\n---\n\n"), "all")}
      >
        {copiedKey === "all"
          ? t("GeneratedPromptView.copiedAllBlocks")
          : t("GeneratedPromptView.copyAllBlocks")}
      </Button>

      {/* 4. How to run on Lovable — primary next step, styled via SimpleMarkdown */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={500}>{t("GeneratedPromptView.howToRunHeading")}</Typography>
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => download("lovable-manual.md", pkg.manual_markdown)}>
            {t("GeneratedPromptView.downloadButton")}
          </Button>
        </Stack>
        <SimpleMarkdown content={pkg.manual_markdown} />
      </Paper>

      {/* 5. Supabase schema — stays monospace (SQL) */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" fontWeight={500}>{t("GeneratedPromptView.supabaseSchemaHeading")}</Typography>
          <Button size="small" startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />} onClick={() => copy(pkg.supabase_schema_sql, "schema")}>
            {copiedKey === "schema" ? t("GeneratedPromptView.copiedSql") : t("GeneratedPromptView.copySql")}
          </Button>
        </Stack>
        <Box component="pre" sx={{ m: 0, p: 2, bgcolor: theme.palette.background.paper, color: theme.palette.text.primary, fontSize: "0.8rem", fontFamily: "monospace", whiteSpace: "pre-wrap", overflow: "auto", borderRadius: 1 }}>
          {pkg.supabase_schema_sql}
        </Box>
      </Paper>

      {/* 6. LLM wiring guide — styled via SimpleMarkdown */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={500} mb={2}>{t("GeneratedPromptView.llmWiringHeading")}</Typography>
        <SimpleMarkdown content={pkg.llm_wiring_guide_markdown} />
      </Paper>

      <Divider />

      {/* 7. Individual blocks — collapsed by default */}
      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, "&:before": { display: "none" }, overflow: "hidden" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, py: 1 }}>
          <Typography fontWeight={500}>
            {t("GeneratedPromptView.viewIndividualBlocks", { count: pkg.blocks.length })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Stack spacing={2} sx={{ p: 2 }}>
            {pkg.blocks.map((block, idx) => (
              <Paper key={block.id} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, bgcolor: "action.hover" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip label={t("GeneratedPromptView.blockLabel", { number: idx + 1 })} size="small" color="primary" />
                    <Typography fontWeight={500}>{block.title}</Typography>
                  </Stack>
                  <Button size="small" startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />} onClick={() => copy(block.prompt, block.id)}>
                    {copiedKey === block.id ? t("GeneratedPromptView.copiedBlock") : t("GeneratedPromptView.copyBlock")}
                  </Button>
                </Stack>
                <Box component="pre" sx={{ m: 0, p: 2, fontSize: "0.82rem", fontFamily: "monospace", whiteSpace: "pre-wrap", overflow: "auto" }}>
                  {block.prompt}
                </Box>
              </Paper>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
